import type { BootServices } from '../bootstrap';
import { getClientSessionId } from '../utils/clientSession';
import { apiFetch } from '../utils/api';

type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

/**
 * Telemetry Errors v1 (client-side, best-effort)
 *
 * Endpoint:
 * - POST /api/telemetry/errors
 *
 * Notes:
 * - We prefer sendBeacon for reliability on Telegram WebView unloads.
 * - sendBeacon can't reliably set custom headers → we pass Supabase JWT in the JSON body (best-effort).
 * - Backend performs PII scrubbing + grouping + idempotency.
 */
export type CaptureErrorInput = {
  type: string;
  message: string;
  stack?: string | null;
  severity?: ErrorSeverity;
  meta?: Record<string, any>;
  // Optional structured fields (when available)
  modelId?: number | null;
  chatSessionId?: string | null;
  player?: Record<string, any> | null;
};

type TelemetryErrorEventV1 = {
  event_id: string;
  occurred_at: string;
  severity: ErrorSeverity;
  type: string;
  message: string;
  stack?: string | null;
  page?: { url?: string; route?: string };
  model?: { model_id?: number | null };
  session?: { chat_session_id?: string | null };
  player?: Record<string, any>;
  meta?: Record<string, any>;
};

let inited = false;
let services: BootServices | null = null;

let queue: TelemetryErrorEventV1[] = [];
let flushTimer: any = null;

function endpoint(): string {
  const cfg = services?.config?.config;
  const url = String(cfg?.errorCollectorUrl || '').trim();
  if (url) return url;
  // safest fallback: same-origin (nginx proxies /api/telemetry/errors to media-server)
  return '/api/telemetry/errors';
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushNow();
  }, 1200);
}

async function flushNow() {
  if (!queue.length) return;
  const batch = queue.slice(0, 20);
  queue = queue.slice(batch.length);

  const env = services?.config?.env || 'test';
  const release =
    String((import.meta as any)?.env?.VITE_RELEASE || (import.meta as any)?.env?.VITE_COMMIT || (import.meta as any)?.env?.VITE_BUILD_ID || '')
      .trim()
      || null;
  const accessToken = (() => {
    try { return services?.supabase?.getAccessToken?.() || null; } catch { return null; }
  })();

  const body = JSON.stringify({
    schema_version: 1,
    app: 'mini-app',
    env,
    release,
    auth: accessToken ? { supabase_access_token: accessToken } : {},
    client: {
      client_session_id: getClientSessionId(),
      ua: (typeof navigator !== 'undefined') ? String(navigator.userAgent || '') : '',
    },
    events: batch,
  });

  try {
    // Prefer sendBeacon when available (reliable on unload).
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const ok = navigator.sendBeacon(endpoint(), new Blob([body], { type: 'application/json' }));
      if (!ok) throw new Error('sendBeacon failed');
      return;
    }
  } catch {
    // fall through to fetch
  }

  try {
    await apiFetch(endpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true as any,
    });
  } catch {
    // best-effort; drop if failed to avoid infinite loops
  }
}

function uuid(): string {
  try {
    const c = (globalThis as any).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch {}
  // Fallback: not perfect, but ok for idempotency in practice.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}

// getClientSessionId moved to utils/clientSession.ts (canonical key + legacy migration)

const dedupTtlMs = 60_000;
const lastSeen: Map<string, number> = new Map();
function isDup(key: string): boolean {
  const now = Date.now();
  const last = lastSeen.get(key) || 0;
  if (now - last < dedupTtlMs) return true;
  lastSeen.set(key, now);
  // cap map size
  if (lastSeen.size > 400) {
    const cutoff = now - dedupTtlMs;
    for (const [k, t] of lastSeen.entries()) {
      if (t < cutoff) lastSeen.delete(k);
      if (lastSeen.size <= 250) break;
    }
  }
  return false;
}

export function captureError(input: CaptureErrorInput): void {
  try {
    const msg = String(input?.message || '').trim();
    if (!msg) return;

    const env = services?.config?.env || '';
    const pageUrl = (typeof window !== 'undefined') ? window.location.href : '';

    const type = String(input.type || 'error').slice(0, 80);
    const severity: ErrorSeverity = (input.severity || 'error') as any;
    const stack = input.stack ? String(input.stack).slice(0, 32000) : null;

    const modelId =
      (typeof input.modelId === 'number')
        ? input.modelId
        : ((typeof (input.meta as any)?.modelId === 'number') ? (input.meta as any).modelId : null);
    const chatSessionId =
      (typeof input.chatSessionId === 'string' && input.chatSessionId)
        ? input.chatSessionId
        : ((typeof (input.meta as any)?.chatSessionId === 'string') ? (input.meta as any).chatSessionId : null);

    // Important: for episode-based incidents we must not dedup different episodes just because
    // type/message/modelId match. If caller provides incident_id, include it in the key.
    const incidentId = (() => {
      try {
        const m = (input.meta || {}) as any;
        const s = String(m?.incident_id || m?.incidentId || '').trim();
        return s || '';
      } catch {
        return '';
      }
    })();
    const key = `${type}|${msg.slice(0, 120)}|${String(modelId ?? '')}|${incidentId}`;
    if (isDup(key)) return;

    const entry: TelemetryErrorEventV1 = {
      event_id: uuid(),
      occurred_at: new Date().toISOString(),
      severity,
      type,
      message: msg.slice(0, 4000),
      stack,
      page: { url: pageUrl },
      model: { model_id: modelId },
      session: { chat_session_id: chatSessionId },
      player: (input.player && typeof input.player === 'object') ? input.player : undefined,
      meta: {
        ...(input.meta || {}),
        env,
      },
    };

    queue.push(entry);
    // cap memory
    if (queue.length > 50) queue = queue.slice(-50);
    scheduleFlush();
  } catch {
    // never throw from monitoring
  }
}

export function initErrorMonitoring(s: BootServices): void {
  if (inited) return;
  inited = true;
  services = s;

  // JS runtime errors
  window.addEventListener('error', (ev: any) => {
    try {
      // Resource errors (img/video/script) sometimes come as Event without .error
      const isResource = !!ev?.target && !!ev?.target?.tagName && !ev?.message;
      if (isResource) {
        const tag = String(ev.target.tagName || '').toLowerCase();
        const src = String(ev.target.currentSrc || ev.target.src || '').trim();
        return captureError({
          type: 'resource_error',
          message: `${tag} load error`,
          severity: 'warning',
          meta: { tag, src }
        });
      }
      captureError({
        type: 'js_error',
        message: String(ev?.message || 'Unknown error'),
        stack: ev?.error?.stack || null,
        meta: { filename: ev?.filename, lineno: ev?.lineno, colno: ev?.colno }
      });
    } catch {}
  }, true);

  // Promise rejections
  window.addEventListener('unhandledrejection', (ev: any) => {
    try {
      const r = ev?.reason;
      const msg =
        (typeof r === 'string') ? r :
        (r?.message ? String(r.message) : 'Unhandled Promise Rejection');
      const stack = r?.stack ? String(r.stack) : null;
      const meta =
        (r && typeof r === 'object' && (r as any).telemetry && typeof (r as any).telemetry === 'object')
          ? { ...(r as any).telemetry }
          : undefined;
      captureError({ type: 'unhandled_promise', message: msg, stack, meta });
    } catch {}
  });

  // Flush on hide/unload
  try {
    window.addEventListener('pagehide', () => { void flushNow(); });
    window.addEventListener('beforeunload', () => { void flushNow(); });
  } catch {}
}


